import type { Section, SectionDesign } from '../model/pages';
import {
  GRID_SECTION_TYPES, SECTION_BACKGROUND_POSITIONS, SECTION_CONTENT_WIDTHS, normalizeSectionDesign,
} from '../model/section-design';
import { ColorField } from './ColorField';
import { SECTION_ALIGNMENT_TYPES, SectionAlignmentField } from './SectionAlignmentFields';
import { Block, HINT, LABEL, RangeField, SelectField, TEXT_BTN } from './InspectorKit';

type On = (patch: Partial<Section>) => void;
type PaddingKey = 'padding_top' | 'padding_bottom' | 'mobile_padding_top' | 'mobile_padding_bottom';
type ColorKey = 'background_color' | 'text_color' | 'heading_color' | 'button_color';

/** Every edit keeps the section's other overrides. Clearing the last one
 * returns to the original page style instead of leaving an empty object. */
export function sectionDesignPatch<K extends keyof SectionDesign>(section: Section, key: K, value: SectionDesign[K]): Partial<Section> {
  return { design: normalizeSectionDesign({ ...section.design, [key]: value }) };
}

const COLORS: ReadonlyArray<{ key: ColorKey; label: string; initial: string }> = [
  { key: 'background_color', label: 'Section background', initial: '#ffffff' },
  { key: 'text_color', label: 'Text color', initial: '#334155' },
  { key: 'heading_color', label: 'Heading color', initial: '#0f172a' },
  { key: 'button_color', label: 'Button color', initial: '#2563eb' },
];
const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/** Unset values stay visibly automatic. The inspector never invents a color
 * or spacing value and presents it as the page's computed appearance. */
function DefaultRow({ label, value, onCustomize }: { label: string; value: string; onCustomize: () => void }) {
  return (
    <div className="pe-section-design-default">
      <div className="min-w-0">
        <span className={LABEL}>{label}</span>
        <span className="pe-section-design-value">{value}</span>
      </div>
      <button type="button" className={TEXT_BTN} aria-label={`Customize ${label.toLowerCase()}`} onClick={onCustomize}>Customize</button>
    </div>
  );
}

function PaddingField({ section, field, label, phone = false, onChange }: {
  section: Section; field: PaddingKey; label: string; phone?: boolean; onChange: On;
}) {
  const design = normalizeSectionDesign(section.design);
  const value = design?.[field];
  const desktopValue = design?.[field.endsWith('top') ? 'padding_top' : 'padding_bottom'];
  const max = phone ? 100 : 160;
  const inherited = phone ? (desktopValue === undefined ? 'From section layout' : `From section layout (${desktopValue}px)`) : 'Automatic';
  const resetLabel = phone ? 'Inherit' : 'Auto';
  if (value === undefined) {
    return <DefaultRow label={label} value={inherited}
      onCustomize={() => onChange(sectionDesignPatch(section, field, Math.min(phone ? (desktopValue ?? 32) : 48, max)))} />;
  }
  return (
    <RangeField label={label} value={value} min={0} max={max} step={1}
      ariaLabel={label} onChange={(next) => onChange(sectionDesignPatch(section, field, next))}
      display={<span className="pe-section-design-range-value"><span>{value}px</span>
        <button type="button" className={TEXT_BTN} aria-label={`Reset ${label.toLowerCase()} to ${phone ? 'inherited' : 'automatic'}`}
          onClick={() => onChange(sectionDesignPatch(section, field, undefined))}>{resetLabel}</button>
      </span>} />
  );
}

export function BackgroundPositionField({ section, onChange }: { section: Section; onChange: On }) {
  const design = normalizeSectionDesign(section.design);
  if (!section.bg_image_url) return null;
  return (
    <SelectField label="Background position" value={design?.background_position ?? ''}
      onChange={(value) => onChange(sectionDesignPatch(section, 'background_position', value ? value as SectionDesign['background_position'] : undefined))}>
      <option value="">Automatic (center)</option>
      {SECTION_BACKGROUND_POSITIONS.map((position) => <option key={position} value={position}>{titleCase(position)}</option>)}
    </SelectField>
  );
}

/** Optional, section-scoped overrides. Separate groups keep the common
 * content fields short while making colors and phone layout easy to find. */
export function SectionDesignFields({ section, onChange }: { section: Section; onChange: On }) {
  const design = normalizeSectionDesign(section.design);
  const isGrid = (GRID_SECTION_TYPES as readonly string[]).includes(section.type);
  return (
    <>
      <Block name="Section layout" title="Layout">
        {SECTION_ALIGNMENT_TYPES.includes(section.type) && <SectionAlignmentField section={section} onChange={onChange} />}
        <SelectField label="Content width" value={design?.content_width ?? ''}
          onChange={(value) => onChange(sectionDesignPatch(section, 'content_width', value ? value as SectionDesign['content_width'] : undefined))}>
          <option value="">Automatic</option>
          {SECTION_CONTENT_WIDTHS.map((width) => <option key={width} value={width}>{titleCase(width)}</option>)}
        </SelectField>
        {isGrid && (
          <SelectField label="Columns" value={design?.columns === undefined ? '' : String(design.columns)}
            onChange={(value) => onChange(sectionDesignPatch(section, 'columns', value ? Number(value) as SectionDesign['columns'] : undefined))}>
            <option value="">Automatic</option>
            {[1, 2, 3, 4].map((count) => <option key={count} value={count}>{count} {count === 1 ? 'column' : 'columns'}</option>)}
          </SelectField>
        )}
        <PaddingField section={section} field="padding_top" label="Top padding" onChange={onChange} />
        <PaddingField section={section} field="padding_bottom" label="Bottom padding" onChange={onChange} />
      </Block>
      <Block name="Section colors" title="Colors">
        {COLORS.map(({ key, label, initial }) => design?.[key] === undefined
          ? <DefaultRow key={key} label={label} value="From page style" onCustomize={() => onChange(sectionDesignPatch(section, key, initial))} />
          : <ColorField key={key} label={label} value={design[key]} onChange={(value) => onChange(sectionDesignPatch(section, key, value))}
            aside={<button type="button" className={TEXT_BTN} aria-label={`Reset ${label.toLowerCase()} to page style`}
              onClick={() => onChange(sectionDesignPatch(section, key, undefined))}>Default</button>} />)}
      </Block>
      <Block name="Phone layout">
        <p className={HINT}>Inherits the section layout until you set a phone override.</p>
        {isGrid && (
          <SelectField label="Phone columns" value={design?.mobile_columns === undefined ? '' : String(design.mobile_columns)}
            onChange={(value) => onChange(sectionDesignPatch(section, 'mobile_columns', value ? Number(value) as SectionDesign['mobile_columns'] : undefined))}>
            <option value="">Automatic for phone</option>
            <option value="1">1 column</option><option value="2">2 columns</option>
          </SelectField>
        )}
        <PaddingField section={section} field="mobile_padding_top" label="Phone top padding" phone onChange={onChange} />
        <PaddingField section={section} field="mobile_padding_bottom" label="Phone bottom padding" phone onChange={onChange} />
      </Block>
    </>
  );
}
